import { Injectable, computed, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { AuthService } from "src/app/auth/services/auth.service";
import { TenantContextService } from "./tenant-context.service";

/**
 * I-5 (2026-05-06) — Super-admin impersonation.
 *
 * Lets a platform operator (super_admin) act as a tenant_admin of a
 * specific organization for support / debugging. The backend issues
 * a short-lived impersonation session token; this service stores
 * the original super_admin token in localStorage so we can
 * "exit impersonation" cleanly.
 *
 * Backend endpoint pending: POST /super/impersonate { organizationId }
 *   → { token, organizationId, expiresAt }
 *
 * Storage keys:
 *   - `Token`             — current effective token (super_admin OR
 *                            impersonation token)
 *   - `OriginalToken`     — saved super_admin token while impersonating
 *   - `ImpersonationOrgId` — orgId of the tenant being impersonated
 *   - `ImpersonationStart` — ISO timestamp impersonation began
 *
 * UI surface: a sticky banner at the top of the layout that shows
 * "Impersonating <tenant>" with an Exit button. See
 * `layout/components/impersonation-banner/`.
 */
@Injectable({ providedIn: "root" })
export class ImpersonationService {
  private readonly _http = inject(HttpClient);
  private readonly _auth = inject(AuthService);
  private readonly _tenantContext = inject(TenantContextService);

  // Reactive flag — components can `*ngIf="!impersonation.isActive()"`
  // or wire computed signals off it. Initialised from localStorage so
  // the state survives page reloads.
  private readonly _active = signal<boolean>(this.readActive());
  readonly isActive = this._active.asReadonly();
  readonly orgId = computed<number | null>(() => {
    const raw = localStorage.getItem("ImpersonationOrgId");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  });
  readonly startedAt = computed<string | null>(() => localStorage.getItem("ImpersonationStart") || null);

  /**
   * Start impersonating a tenant. Saves the current super_admin
   * token, swaps in the impersonation token, reloads the tenant
   * context so signals (`isTenantAdmin`, `canManageOperations`,
   * etc.) reflect the impersonated org.
   *
   * 2026-05-07 — On production builds we surface a confirm dialog
   * showing the TTL before swapping tokens. Dev builds keep the
   * fast loop (silent start, banner only) so operators can iterate
   * without an extra click each time.
   */
  async start(organizationId: number): Promise<void> {
    if (this._active()) {
      throw new Error("Already impersonating — exit first.");
    }

    if (environment.production) {
      const ttlMinutes = 60;
      const confirmed = window.confirm(
        `You are about to impersonate organization #${organizationId}.\n\n` +
          `Your super-admin session will be paused and replaced with a tenant-admin token ` +
          `valid for ${ttlMinutes} minutes. Every action you take will be audit-logged ` +
          `against your super-admin identity.\n\nContinue?`
      );
      if (!confirmed) {
        return;
      }
    }

    const res: any = await firstValueFrom(
      this._http.post(`${environment.meetApiHost}super/impersonate`, { organizationId }, this._auth.userToken())
    );
    const token = res?.data?.token ?? res?.token;
    if (!token) throw new Error("Backend did not return an impersonation token.");

    const originalToken = localStorage.getItem("Token") || "";
    localStorage.setItem("OriginalToken", originalToken);
    localStorage.setItem("Token", token);
    localStorage.setItem("ImpersonationOrgId", String(organizationId));
    localStorage.setItem("ImpersonationStart", new Date().toISOString());
    this._active.set(true);

    // Re-load memberships under the new token so the rest of the
    // app sees tenant_admin role + matching X-Org-Id.
    await this._tenantContext.load();
  }

  /**
   * Exit impersonation. Restores the saved super_admin token and
   * reloads tenant memberships. If no original token is stored
   * (browser cleared it), forces a logout to recover.
   */
  async exit(): Promise<void> {
    const original = localStorage.getItem("OriginalToken");
    if (!original) {
      // Recovery: forced logout. The session is in an inconsistent
      // state and we don't have the super_admin token to restore.
      localStorage.removeItem("Token");
      localStorage.removeItem("ImpersonationOrgId");
      localStorage.removeItem("ImpersonationStart");
      this._active.set(false);
      window.location.href = "/login";
      return;
    }
    localStorage.setItem("Token", original);
    localStorage.removeItem("OriginalToken");
    localStorage.removeItem("ImpersonationOrgId");
    localStorage.removeItem("ImpersonationStart");
    this._active.set(false);
    await this._tenantContext.load();
  }

  private readActive(): boolean {
    return !!localStorage.getItem("OriginalToken") && !!localStorage.getItem("ImpersonationOrgId");
  }
}
